var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useBackgroundContext } from '../BackgroundContext/index.js';
import { Icon } from '../Icon/index.js';
import { SpinnerIcon } from '../icons/index.js';
export const BUTTON_VARIANTS = [
    'primary',
    'secondary',
    'outline',
    'danger',
    'basic',
];
export const BUTTON_SIZES = ['xs', 'sm', 'md', 'lg', 'xl'];
export const Button = forwardRef((props, ref) => {
    const { as, variant = 'primary', size = 'md', fullWidth = false, rounded = false, disabled = false, loading = false, className, children, leadingIcon, trailingIcon } = props, rest = __rest(props, ["as", "variant", "size", "fullWidth", "rounded", "disabled", "loading", "className", "children", "leadingIcon", "trailingIcon"]);
    const { backgroundType } = useBackgroundContext();
    const sizeClsx = [
        'font-medium shadow-sm',
        size === 'xs' && 'px-2.5 py-1.5 text-xs',
        size === 'sm' && 'px-3 py-2 text-sm leading-4',
        size === 'md' && 'px-4 py-2 text-sm',
        size === 'lg' && 'px-4 py-2 text-base',
        size === 'xl' && 'px-6 py-3 text-base',
    ];
    const roundedSizeClsx = [
        'font-medium shadow-sm',
        size === 'xs' && 'px-1.5 py-1.5 text-xs',
        size === 'sm' && 'px-2 py-2 text-sm leading-4',
        size === 'md' && 'px-2 py-2 text-sm',
        size === 'lg' && 'px-2 py-2 text-base',
        size === 'xl' && 'px-3 py-3 text-base',
    ];
    const textColorClsx = [
        variant === 'primary' &&
            'text-basicPrimary-500 bg-actionPrimary-500 hover:bg-actionPrimary-600',
        variant === 'secondary' &&
            'text-basicSecondary-500 bg-actionSecondary-100 hover:bg-actionSecondary-200',
        variant === 'outline' && [
            backgroundType === 'main' &&
                'text-basicSurface-500 bg-surface-50 hover:bg-surface-100',
            backgroundType === 'surface' &&
                'text-basicSurface-500 bg-surface-50 hover:bg-surface-100',
            backgroundType === 'secondary' &&
                'text-basicSecondary-500 bg-actionSecondary-50 hover:bg-actionSecondary-100',
        ],
        variant === 'basic' && [
            backgroundType === 'main' &&
                'text-basicMain-500 bg-main-50 hover:bg-main-100',
            backgroundType === 'surface' &&
                'text-basicSurface-500 bg-surface-50 hover:bg-surface-100',
            backgroundType === 'secondary' &&
                'text-basicSecondary-500  bg-actionSecondary-50 hover:bg-actionSecondary-100',
        ],
        variant === 'danger' && 'text-white bg-danger-500 hover:bg-danger-600 ',
    ];
    const borderClsx = [
        'border',
        variant === 'primary' && 'border-transparent',
        variant === 'secondary' && 'border-transparent',
        variant === 'outline' && [
            backgroundType === 'main' && 'border-basicMain-300/25',
            backgroundType === 'surface' && 'border-basicSurface-300/25',
            backgroundType === 'secondary' && 'border-basicSecondary-300/25',
        ],
        variant === 'basic' && ['shadow-none border-none'],
        variant === 'danger' && 'border-transparent focus:ring-danger-400',
    ];
    const roundedClsx = [
        !rounded && size === 'xs' && 'rounded',
        !rounded && size !== 'xs' && 'rounded-md',
        rounded && 'rounded-full ',
    ];
    const contentProps = { trailingIcon, leadingIcon, size, children };
    const Component = as || 'button';
    return (React.createElement(Component, Object.assign({ ref: ref, type: as ? undefined : 'button', className: twMerge(clsx('inline-flex items-center relative', 'focus:outline-none focus-visible:ring', textColorClsx, !rounded ? sizeClsx : roundedSizeClsx, roundedClsx, borderClsx, fullWidth && 'w-full flex justify-center', disabled && 'opacity-60 cursor-default pointer-events-none', loading && 'opacity-60 cursor-default pointer-events-none', className)) }, rest),
        React.createElement(React.Fragment, null,
            loading && (React.createElement("div", { className: "absolute inline-flex left-0 right-0 justify-center" },
                React.createElement(SpinnerIcon, { className: "animate-spin" }))),
            React.createElement("span", { className: clsx('flex', { invisible: loading }) },
                React.createElement(ButtonContent, Object.assign({}, contentProps))))));
});
const ButtonContent = (props) => {
    const { leadingIcon, trailingIcon, children, size } = props;
    const iconSizeClsx = [
        size === 'xs' && 'h-4 w-4',
        size === 'sm' && 'h-4 w-4',
        size === 'md' && 'h-5 w-5',
        size === 'lg' && 'h-6 w-6',
        size === 'xl' && 'h-6 w-6',
    ];
    const leadingIconPaddingClsx = [
        ['xs', 'sm'].includes(size) && '-ml-0.5 mr-2',
        ['md'].includes(size) && '-ml-1 mr-2',
        ['lg', 'xl'].includes(size) && '-ml-1 mr-3',
    ];
    const trailingIconPaddingClsx = [
        ['xs', 'sm'].includes(size) && 'ml-2 -mr-0.5',
        ['md'].includes(size) && 'ml-2 -mr-1 ',
        ['lg', 'xl'].includes(size) && 'ml-3 -mr-1 ',
    ];
    return (React.createElement("span", { className: "inline-flex items-center" },
        leadingIcon && (React.createElement(Icon, { className: clsx(iconSizeClsx, !!children && leadingIconPaddingClsx) }, leadingIcon)),
        children,
        trailingIcon && (React.createElement(Icon, { className: clsx(iconSizeClsx, !!children && trailingIconPaddingClsx) }, trailingIcon))));
};
//# sourceMappingURL=Button.js.map